import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent } from "../../components/ui/tabs";
import { useParams } from "react-router";
import apiService from "../../services/apiService";
import { toast } from "../../hooks/use-toast";
import { useEffect } from "react";
import UploadDebtProfileModal from "../../components/debtprofile/UploadDebtProfileModal";
import { FinancialDataType } from "../../lib/csvUtils";
import { random } from "lodash";
import { Download } from "lucide-react";
// import SimpleDateRangePicker from "../../components/ui/SimpleDateRangePicker";
import SimpleDatePicker from "../../components/SimpleDatePicker";

interface ApiResponse<T> {
  data: T[];
  selectedDate:Date;
}
interface DebtData {
  key: string;
  sr?: number;
  nature: string;
  loanType: string;
  bankName: string;
  totalLoanAmount: number;
  emi: number;
  loanStartDate: string;
  roi: number;
  outstandingAsOnDate: number;
  totalTenure: number;
}

// Format date function
const formatDate = (dateString: string | null) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

// Get initials for avatar
const getInitials = (name?: string | null): string => {
  if (!name) return "";
  if (name == null) return "";

  return name
    .split(" ")
    .map(part => part.charAt(0))
    .join("")
    .toUpperCase();
};

export default function DebtProfile() {

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareDebtData, setShareDebtData] = useState<DebtData[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const { customerId } = useParams<{ customerId: string }>();
  const [isUserDateChange, setIsUserDateChange] = useState(false);

  const handleDateChange = (date: Date | null | undefined) => {
    if (date) {
      setIsUserDateChange(true);
      setSelectedDate(date);
    } else {
      setSelectedDate(undefined);
    }
  };

  
  const formatDateOnly = (date: Date) => {
    return date?.toISOString().split("T")[0]; // "YYYY-MM-DD"
  };

  const handleDataSave = (type: FinancialDataType, data: any) => {
    toast({
      title: "Financial Data Updated",
      description: `${type.replace('-', ' ').charAt(0).toUpperCase() + type.replace('-', ' ').slice(1)} data has been updated.`
    });
    apiService.post(`/customers/uplaod-debts/${customerId}`, { data: data })
      .then(() => {
        fetchDebt();
        setIsUploadModalOpen(false);
      })
      .catch((error) => {
        setIsUploadModalOpen(false);
      });
  };

 useEffect(() => {
  if (selectedDate) {
    const formattedDate = formatDateOnly(selectedDate);
    apiService
      .post(`/customers/debtsupdatedate/${customerId}`, {
        selectedDate: formattedDate,
      })
      .catch((error) => {
        console.error("Failed to save selected date:", error);
      });
  }
}, [selectedDate]);

  const fetchDebt = () => {
    apiService
      .get<ApiResponse<DebtData>>(`/customers/debts?customerId=${customerId}`)
      .then((response) => {
        setShareDebtData(response.data);
        if (response.selectedDate) {
          setSelectedDate(new Date(response.selectedDate)); // ✅ ensure it's a Date object
        }
      });
  };

  useEffect(() => {
    fetchDebt();
  }, [customerId]);

  const downloadCSV = (data: any[], filename = "data.csv") => {
    if (!data || data.length === 0) return;

    const sanitizedData = data.map(({ id, createdAt, updatedAt, ...rest }) => rest);

    const headers = Object.keys(sanitizedData[0]);
    const csvRows = [
      headers.join(","), // header row
      ...data.map(row =>
        headers.map(field => {
          let val = row[field];
          if (typeof val === "string") {
            // Escape double quotes
            val = val.replace(/"/g, '""');
          }
          return `"${val}"`;
        }).join(",")
      ),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  function handleDownloadDebtProfile(): void {
    downloadCSV(shareDebtData, "debt.csv");
  }

  const totalLoanAmount = shareDebtData.reduce((sum, row) => sum + (row.totalLoanAmount || 0), 0);
  const totalEmi = shareDebtData.reduce((sum, row) => sum + (row.emi || 0), 0);
  const totalOutstanding = shareDebtData.reduce((sum, row) => sum + (row.outstandingAsOnDate || 0), 0);

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Debt Profile</h1>
            <p className="text-gray-600">View all debt profile</p>
          </div>

          {/* Date Picker */}
          <div className="space-y-2">
            <label className="text-sm font-medium">As on Date</label>
<SimpleDatePicker
  selectedDate={selectedDate}
  onDateChange={handleDateChange} // ✅ use this instead of direct setter
  className="w-full"
  placeholder="Pick a date"
/>
          </div>
        </div>

        <div>
          <Button
            className="bg-[#489BA8] text-white hover:bg-[#489BA8]/80 mr-3"
            onClick={() => setIsUploadModalOpen(true)}
          >
            <span className="mr-2">+</span> Add Debt Profile
          </Button>
          {shareDebtData.length > 0 && (
            <div className="float-right mb-4 hover:cursor-pointer hover:text-red-600 mt-2"
              onClick={() => handleDownloadDebtProfile()}
            >
              <Download className="mr-2" />
            </div>
          )}
        </div>
      </div>

      {/* Tabs for Directors, Shareholding, Related Party, and Securities Allotment */}
      <Tabs value="debt-profile" className="w-full mb-6">
        <TabsContent value="debt-profile">
          {/* Directors Table */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : (
            <>
              {shareDebtData.length > 0 ? (
                <div className="rounded-lg border overflow-hidden">
                  <Table className="border rounded-md">
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Nature</TableHead>
                        <TableHead className="font-semibold">Loan Type</TableHead>
                        <TableHead className="font-semibold">Lender Name</TableHead>
                        <TableHead className="font-semibold">Total Loan Amount</TableHead>
                        <TableHead className="font-semibold">EMI</TableHead>
                        <TableHead className="font-semibold">Loan Start Date</TableHead>
                        <TableHead className="font-semibold">ROI (%)</TableHead>
                        <TableHead className="font-semibold">Outstanding</TableHead>
                        <TableHead className="font-semibold">Total Tenure</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shareDebtData.map((debtProfile) => (
                        <TableRow key={random(1, 1000000)} className="hover:bg-gray-50 transition-colors">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
                                {getInitials(debtProfile.nature)}
                              </div>
                              <div>
                                <p className="font-medium">{debtProfile.nature ?? "-"}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {debtProfile.loanType ? (<Badge className={debtProfile.loanType === "Director" ?
                              "bg-blue-100 text-blue-700 hover:bg-blue-200" :
                              "bg-purple-100 text-purple-700 hover:bg-purple-200"}>
                              {debtProfile.loanType ?? "-"}
                            </Badge>) : "-" }
                            
                          </TableCell>
                          <TableCell>{debtProfile.bankName ?? "-"}</TableCell>
                          <TableCell>
                            {debtProfile.totalLoanAmount != null
                              ? debtProfile.totalLoanAmount.toLocaleString("en-IN")
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {debtProfile.emi != null
                              ? debtProfile.emi.toLocaleString("en-IN")
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {formatDate(debtProfile.loanStartDate) ?? "-"}
                          </TableCell>
                          <TableCell>
                            {debtProfile.roi ?? "-"}
                          </TableCell>
                          <TableCell>
                            {debtProfile.outstandingAsOnDate != null
                              ? debtProfile.outstandingAsOnDate.toLocaleString("en-IN")
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {debtProfile.totalTenure != null
                              ? debtProfile.totalTenure.toLocaleString("en-IN")
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}

                      <TableRow className="bg-gray-100 font-semibold">
                        <TableCell colSpan={3}>Total</TableCell>
                        <TableCell>{totalLoanAmount?.toLocaleString("en-IN") ?? "-"}</TableCell>
                        <TableCell>{totalEmi?.toLocaleString("en-IN") ?? "-"}</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>{totalOutstanding?.toLocaleString("en-IN") ?? "-"}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  Add the debt profile to access details of the company's borrowings.
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Financials Modal */}
      <UploadDebtProfileModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onDataSave={handleDataSave}
      />
    </div>
  );
}